import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '@/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LogOut, Download, Trash2, Users, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [usersRes, providersRes] = await Promise.all([
        adminAPI.getUsers(),
        adminAPI.getProviders()
      ]);
      setUsers(usersRes.data);
      setProviders(providersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteUserId) return;

    try {
      await adminAPI.deleteUser(deleteUserId);
      toast.success('User deleted successfully');
      setDeleteUserId(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleExport = async () => {
    try {
      const response = await adminAPI.exportData();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'users_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Data exported successfully');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-[#4B5563]">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-border/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className="font-heading text-2xl font-bold text-primary">Admin Panel</h1>
            <div className="flex items-center gap-4">
              <Button
                data-testid="export-btn"
                onClick={handleExport}
                className="bg-accent hover:bg-accent-hover text-white rounded-full"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button
                data-testid="logout-btn"
                onClick={handleLogout}
                variant="ghost"
                className="text-[#4B5563] hover:text-primary hover:bg-secondary/50"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Stats */}
      <div className="py-8 bg-gradient-to-b from-secondary to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl border border-border/40 p-6">
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-6 h-6 text-primary" />
                <h3 className="font-heading text-lg font-semibold text-[#2C3333]">Total Users</h3>
              </div>
              <p className="text-4xl font-heading font-bold text-primary" data-testid="total-users">{users.length}</p>
            </div>
            <div className="bg-white rounded-2xl border border-border/40 p-6">
              <div className="flex items-center gap-3 mb-2">
                <UserCheck className="w-6 h-6 text-primary" />
                <h3 className="font-heading text-lg font-semibold text-[#2C3333]">Providers</h3>
              </div>
              <p className="text-4xl font-heading font-bold text-primary" data-testid="total-providers">{providers.length}</p>
            </div>
            <div className="bg-white rounded-2xl border border-border/40 p-6">
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-6 h-6 text-primary" />
                <h3 className="font-heading text-lg font-semibold text-[#2C3333]">Seekers</h3>
              </div>
              <p className="text-4xl font-heading font-bold text-primary" data-testid="total-seekers">
                {users.filter(u => u.role === 'seeker').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tables */}
      <div className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="users" className="w-full">
            <TabsList className="mb-6">
              <TabsTrigger value="users" data-testid="users-tab">All Users</TabsTrigger>
              <TabsTrigger value="providers" data-testid="providers-tab">Providers</TabsTrigger>
            </TabsList>

            <TabsContent value="users">
              <div className="bg-white rounded-2xl border border-border/40 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Created At</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-[#9CA3AF]">No users found</TableCell>
                      </TableRow>
                    ) : (
                      users.map((user, index) => (
                        <TableRow key={user.id} data-testid={`user-row-${index}`}>
                          <TableCell className="font-medium">{user.name}</TableCell>
                          <TableCell>{user.phone}</TableCell>
                          <TableCell>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              user.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                              user.role === 'provider' ? 'bg-primary/10 text-primary' :
                              'bg-accent/10 text-accent'
                            }`}>
                              {user.role}
                            </span>
                          </TableCell>
                          <TableCell>{new Date(user.created_at).toLocaleDateString()}</TableCell>
                          <TableCell className="text-right">
                            {user.role !== 'admin' && (
                              <Button
                                data-testid={`delete-user-btn-${index}`}
                                onClick={() => setDeleteUserId(user.id)}
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="providers">
              <div className="bg-white rounded-2xl border border-border/40 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>City</TableHead>
                      <TableHead>Experience</TableHead>
                      <TableHead>Specializations</TableHead>
                      <TableHead>Session Type</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {providers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-[#9CA3AF]">No providers found</TableCell>
                      </TableRow>
                    ) : (
                      providers.map((provider, index) => (
                        <TableRow key={provider.user.id} data-testid={`provider-row-${index}`}>
                          <TableCell className="font-medium">{provider.user.name}</TableCell>
                          <TableCell>{provider.profile.city}</TableCell>
                          <TableCell>{provider.profile.experience} years</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {provider.profile.specializations.slice(0, 2).map(spec => (
                                <span key={spec} className="bg-secondary text-primary px-2 py-1 rounded text-xs">
                                  {spec}
                                </span>
                              ))}
                              {provider.profile.specializations.length > 2 && (
                                <span className="text-xs text-[#9CA3AF]">
                                  +{provider.profile.specializations.length - 2}
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{provider.profile.session_type}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteUserId} onOpenChange={() => setDeleteUserId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the user and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-btn"
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboard;